import { useState } from 'react';
import { Key, RefreshCw, Copy, Save, Shield, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import Alert from '../components/common/Alert';
import KeyboardDismissView from '../components/common/KeyboardDismissView';

export default function SecuritySetupPage() {
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [keyData, setKeyData] = useState({
    generatedKey: '',
    customKey: '',
    useCustomKey: false,
    copied: false
  });

  const handleGenerateKey = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await authAPI.generateEncryptionKey();
      setKeyData(prev => ({
        ...prev,
        generatedKey: data.data.key,
        useCustomKey: false,
        copied: false
      }));
    } catch (err) {
      setError(err.error || 'Failed to generate key');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async () => {
    const keyToSave = keyData.useCustomKey ? keyData.customKey : keyData.generatedKey;

    if (!keyToSave || keyToSave.length < 32) {
      setError('Encryption key must be at least 32 characters');
      return;
    }

    if (!keyData.copied && !keyData.useCustomKey) {
      setError('Please copy and save your key before proceeding');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const { data } = await authAPI.setEncryptionKey(keyToSave);

      // Store encryption key in localStorage for API requests
      if (data.data.encryptionKey) {
        localStorage.setItem('encryptionKey', data.data.encryptionKey);
      }

      setSuccess('Encryption key saved successfully!');
      // Refresh user to update hasEncryptionKey status
      await refreshUser();
    } catch (err) {
      setError(err.error || 'Failed to save encryption key');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setKeyData(prev => ({ ...prev, copied: true }));
    setSuccess('Key copied to clipboard! Save it in a secure location.');
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <KeyboardDismissView className="min-h-[100dvh] bg-gradient-to-br from-surface-50 via-primary-50/30 to-surface-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-xl">
        <div className="bg-white rounded-2xl border border-surface-200 p-8 shadow-xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg"
              style={{ backgroundColor: 'var(--accent-bg)' }}
            >
              <Shield className="w-10 h-10" style={{ color: 'var(--accent-text)' }} />
            </div>
            <h1 className="text-3xl font-bold text-surface-900 mb-3">
              Secure Your Finances
            </h1>
            <p className="text-surface-600 text-lg leading-relaxed">
              Your financial data is protected with <span className="font-semibold text-surface-800">AES-256 encryption</span>. Create your unique encryption key to keep your data safe and private.
            </p>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-6 mb-8 py-4 border-y border-surface-100">
            <div className="text-center">
              <div className="text-2xl font-bold text-income-600">256-bit</div>
              <div className="text-xs text-surface-500">Encryption</div>
            </div>
            <div className="w-px h-10 bg-surface-200"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-income-600">Zero</div>
              <div className="text-xs text-surface-500">Data Leaks</div>
            </div>
            <div className="w-px h-10 bg-surface-200"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-income-600">100%</div>
              <div className="text-xs text-surface-500">Private</div>
            </div>
          </div>

          {success && (
            <Alert
              variant="success"
              message={success}
              onClose={() => setSuccess('')}
            />
          )}
          {error && (
            <Alert
              variant="error"
              message={error}
              onClose={() => setError('')}
            />
          )}

          <div className="space-y-6 mt-6">
            {/* Section Title */}
            <div className="text-center">
              <h2 className="text-lg font-semibold text-surface-800 mb-1">Create Your Encryption Key</h2>
              <p className="text-sm text-surface-500">Choose how you'd like to secure your data</p>
            </div>

            {/* Option buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleGenerateKey}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-4 border-2 border-income-200 bg-income-50 text-income-700 hover:bg-income-100 rounded-xl font-semibold transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Generate Secure Key
              </button>
              <button
                onClick={() => setKeyData(prev => ({ ...prev, useCustomKey: !prev.useCustomKey, generatedKey: '' }))}
                className={`flex-1 px-4 py-4 border-2 rounded-xl font-semibold transition-all ${
                  keyData.useCustomKey
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-surface-200 text-surface-700 hover:bg-surface-50'
                }`}
              >
                Use My Own Key
              </button>
            </div>

            {/* Generated Key Display */}
            {keyData.generatedKey && !keyData.useCustomKey && (
              <div className="p-5 bg-gradient-to-br from-surface-100 to-surface-50 rounded-xl border border-surface-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-income-600" />
                    <span className="text-sm font-semibold text-surface-800">Your Unique Encryption Key</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(keyData.generatedKey)}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-white border border-surface-200 text-surface-700 hover:text-surface-900 rounded-lg hover:bg-surface-50 transition-all shadow-sm"
                  >
                    {keyData.copied ? <Check className="w-4 h-4 text-income-600" /> : <Copy className="w-4 h-4" />}
                    {keyData.copied ? 'Copied!' : 'Copy Key'}
                  </button>
                </div>
                <code className="block p-4 bg-surface-900 text-surface-100 rounded-lg text-sm font-mono break-all select-all">
                  {keyData.generatedKey}
                </code>
                <p className="text-xs text-surface-500 mt-3 text-center">
                  This 256-bit key is cryptographically secure and unique to you
                </p>
              </div>
            )}

            {/* Custom Key Input */}
            {keyData.useCustomKey && (
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">
                  Enter your custom encryption key:
                </label>
                <input
                  type="text"
                  value={keyData.customKey}
                  onChange={(e) => setKeyData(prev => ({ ...prev, customKey: e.target.value }))}
                  placeholder="Enter at least 32 characters..."
                  enterKeyHint="done"
                  className="w-full px-4 py-3 border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                />
                <p className="text-xs text-surface-500 mt-1">
                  {keyData.customKey.length}/32 characters (minimum)
                </p>
              </div>
            )}

            {/* Security Notice */}
            <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-amber-800 mb-2">Keep Your Key Safe</p>
                  <ul className="text-amber-700 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500">✓</span>
                      <span>Save in a password manager for safekeeping</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500">✓</span>
                      <span>This key is set once and becomes your permanent data lock</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-500">✓</span>
                      <span>Your key is stored securely and used only to protect your data</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Save Button */}
            {(keyData.generatedKey || (keyData.useCustomKey && keyData.customKey.length >= 32)) && (
              <button
                onClick={handleSaveKey}
                disabled={loading || (!keyData.copied && !keyData.useCustomKey)}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
              >
                <Save className="w-5 h-5" />
                {loading ? 'Securing Your Account...' : 'Activate Protection & Continue'}
              </button>
            )}

            {keyData.generatedKey && !keyData.copied && !keyData.useCustomKey && (
              <p className="text-sm text-center text-surface-500">
                Copy your encryption key to continue
              </p>
            )}
          </div>
        </div>

        {/* Footer Trust Badge */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-sm text-surface-500 flex items-center justify-center gap-2">
            <Shield className="w-4 h-4" />
            Your privacy is our priority
          </p>
          <p className="text-xs text-surface-400">
            End-to-end encrypted • Zero-knowledge architecture • Your data, your control
          </p>
        </div>
      </div>
    </KeyboardDismissView>
  );
}
